import { useEditor, useEditorState, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Link from '@tiptap/extension-link';
import Underline from '@tiptap/extension-underline';
import { TextStyleKit } from '@tiptap/extension-text-style';
import TextAlign from '@tiptap/extension-text-align';
import { forwardRef, useImperativeHandle, useEffect } from 'react';
import { useUnistyles } from 'react-native-unistyles';
import './QuillEditor.css';

const ToolbarButton = ({ onClick, isActive, title, children, colors }) => (
    <button
        type="button"
        title={title}
        onClick={onClick}
        style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: 32,
            height: 32,
            borderRadius: 6,
            border: 'none',
            cursor: 'pointer',
            backgroundColor: isActive ? colors.primaryContainer : 'transparent',
            color: isActive ? colors.primary : colors.textSecondary,
            transition: 'background 0.15s',
            flexShrink: 0,
        }}
        onMouseEnter={(e) => {
            if (!isActive) e.currentTarget.style.backgroundColor = colors.surface;
        }}
        onMouseLeave={(e) => {
            if (!isActive) e.currentTarget.style.backgroundColor = 'transparent';
        }}
    >
        {children}
    </button>
);

const Divider = ({ colors }) => (
    <div style={{ width: 1, height: 20, backgroundColor: colors.border, margin: '0 2px', flexShrink: 0 }} />
);

const TiptapEditor = forwardRef(({ value, onChange, _placeholder, autoFocus, enableFontSize = false, enableTextAlign = false }, ref) => {
    const { theme: { colors, fonts, roundness } } = useUnistyles();

    const editor = useEditor({
        extensions: [
            StarterKit,
            Underline,
            Link.configure({
                openOnClick: false,
            }),
            ...(enableFontSize || enableTextAlign ? [TextStyleKit] : []),
            ...(enableTextAlign ? [TextAlign.configure({ types: ['heading', 'paragraph'] })] : []),
        ],
        content: value || '',
        autofocus: autoFocus ? 'end' : false,
        onUpdate: ({ editor }) => {
            if (onChange) onChange(editor.getHTML());
        },
    });

    // TipTap v3 doesn't re-render useEditor on transactions — subscribe to the
    // toolbar state explicitly so mark buttons highlight on toggle/selection.
    const editorState = useEditorState({
        editor,
        selector: (ctx) => ({
            bold: ctx.editor.isActive('bold'),
            italic: ctx.editor.isActive('italic'),
            strike: ctx.editor.isActive('strike'),
            code: ctx.editor.isActive('code'),
            underline: ctx.editor.isActive('underline'),
            link: ctx.editor.isActive('link'),
            h1: ctx.editor.isActive('heading', { level: 1 }),
            h2: ctx.editor.isActive('heading', { level: 2 }),
            h3: ctx.editor.isActive('heading', { level: 3 }),
            bulletList: ctx.editor.isActive('bulletList'),
            orderedList: ctx.editor.isActive('orderedList'),
            blockquote: ctx.editor.isActive('blockquote'),
            fontSize: ctx.editor.getAttributes('textStyle').fontSize || '',
            alignLeft: ctx.editor.isActive({ textAlign: 'left' }),
            alignCenter: ctx.editor.isActive({ textAlign: 'center' }),
            alignRight: ctx.editor.isActive({ textAlign: 'right' }),
        }),
    });

    useImperativeHandle(ref, () => ({
        getEditor: () => editor,
        focus: () => editor?.commands?.focus?.('end'),
    }));

    useEffect(() => {
        if (editor && value !== editor.getHTML()) {
            editor.commands.setContent(value || '');
        }
    }, [value, editor]);

    useEffect(() => {
        if (autoFocus && editor) {
            editor.commands.focus('end');
        }
    }, [autoFocus, editor]);

    if (!editor) return null;

    const handleLink = () => {
        const url = window.prompt('Enter URL:', 'https://');
        if (url) editor.chain().focus().setLink({ href: url }).run();
        else editor.chain().focus().unsetLink().run();
    };

    return (
        <div style={{
            border: `1px solid ${colors.border}`,
            borderRadius: roundness.sm,
            overflow: 'hidden',
            fontFamily: fonts.body,
        }}>
            {/* Toolbar */}
            <div style={{
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'center',
                flexWrap: 'wrap',
                gap: 2,
                padding: '6px 8px',
                backgroundColor: colors.surface,
                borderBottom: `1px solid ${colors.border}`,
            }}>
                {/* Undo / Redo */}
                <ToolbarButton title="Undo" onClick={() => editor.chain().focus().undo().run()} colors={colors}>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 7v6h6"/><path d="M21 17a9 9 0 0 0-9-9 9 9 0 0 0-6 2.3L3 13"/></svg>
                </ToolbarButton>
                <ToolbarButton title="Redo" onClick={() => editor.chain().focus().redo().run()} colors={colors}>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 7v6h-6"/><path d="M3 17a9 9 0 0 1 9-9 9 9 0 0 1 6 2.3L21 13"/></svg>
                </ToolbarButton>

                <Divider colors={colors} />

                {/* Headings */}
                <ToolbarButton title="Heading 1" isActive={editorState?.h1} onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()} colors={colors}>
                    <span style={{ fontFamily: 'serif', fontWeight: 'bold', fontSize: 13 }}>H1</span>
                </ToolbarButton>
                <ToolbarButton title="Heading 2" isActive={editorState?.h2} onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()} colors={colors}>
                    <span style={{ fontFamily: 'serif', fontWeight: 'bold', fontSize: 13 }}>H2</span>
                </ToolbarButton>
                <ToolbarButton title="Heading 3" isActive={editorState?.h3} onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()} colors={colors}>
                    <span style={{ fontFamily: 'serif', fontWeight: 'bold', fontSize: 13 }}>H3</span>
                </ToolbarButton>

                <Divider colors={colors} />

                {/* Lists */}
                <ToolbarButton title="Bullet list" isActive={editorState?.bulletList} onClick={() => editor.chain().focus().toggleBulletList().run()} colors={colors}>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="9" y1="6" x2="20" y2="6"/><line x1="9" y1="12" x2="20" y2="12"/><line x1="9" y1="18" x2="20" y2="18"/><circle cx="4" cy="6" r="1" fill="currentColor" stroke="none"/><circle cx="4" cy="12" r="1" fill="currentColor" stroke="none"/><circle cx="4" cy="18" r="1" fill="currentColor" stroke="none"/></svg>
                </ToolbarButton>
                <ToolbarButton title="Ordered list" isActive={editorState?.orderedList} onClick={() => editor.chain().focus().toggleOrderedList().run()} colors={colors}>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="10" y1="6" x2="21" y2="6"/><line x1="10" y1="12" x2="21" y2="12"/><line x1="10" y1="18" x2="21" y2="18"/><path d="M4 6h1v4"/><path d="M4 10h2"/><path d="M6 18H4c0-1 2-2 2-3s-1-1.5-2-1"/></svg>
                </ToolbarButton>
                <ToolbarButton title="Blockquote" isActive={editorState?.blockquote} onClick={() => editor.chain().focus().toggleBlockquote().run()} colors={colors}>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><path d="M3 21c3 0 7-1 7-8V5c0-1.25-.756-2.017-2-2H4c-1.25 0-2 .75-2 1.972V11c0 1.25.75 2 2 2 1 0 1 0 1 1v1c0 1-1 2-2 2s-1 .008-1 1.031V20c0 1 0 1 1 1z"/><path d="M15 21c3 0 7-1 7-8V5c0-1.25-.757-2.017-2-2h-4c-1.25 0-2 .75-2 1.972V11c0 1.25.75 2 2 2h.75c0 2.25.25 4-2.75 4v3c0 1 0 1 1 1z"/></svg>
                </ToolbarButton>

                <Divider colors={colors} />

                {/* Inline formatting */}
                <ToolbarButton title="Bold" isActive={editorState?.bold} onClick={() => editor.chain().focus().toggleBold().run()} colors={colors}>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M6 4h8a4 4 0 0 1 4 4 4 4 0 0 1-4 4H6z"/><path d="M6 12h9a4 4 0 0 1 4 4 4 4 0 0 1-4 4H6z"/></svg>
                </ToolbarButton>
                <ToolbarButton title="Italic" isActive={editorState?.italic} onClick={() => editor.chain().focus().toggleItalic().run()} colors={colors}>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="19" y1="4" x2="10" y2="4"/><line x1="14" y1="20" x2="5" y2="20"/><line x1="15" y1="4" x2="9" y2="20"/></svg>
                </ToolbarButton>
                <ToolbarButton title="Strikethrough" isActive={editorState?.strike} onClick={() => editor.chain().focus().toggleStrike().run()} colors={colors}>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="4" y1="12" x2="20" y2="12"/><path d="M16 6C16 6 14.5 4 12 4s-5 1-5 4c0 4 5 4 5 4"/><path d="M8 18c0 0 1.5 2 4 2s5-1 5-4"/></svg>
                </ToolbarButton>
                <ToolbarButton title="Code" isActive={editorState?.code} onClick={() => editor.chain().focus().toggleCode().run()} colors={colors}>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="16 18 22 12 16 6"/><polyline points="8 6 2 12 8 18"/></svg>
                </ToolbarButton>
                <ToolbarButton title="Underline" isActive={editorState?.underline} onClick={() => editor.chain().focus().toggleUnderline().run()} colors={colors}>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M6 3v7a6 6 0 0 0 6 6 6 6 0 0 0 6-6V3"/><line x1="4" y1="21" x2="20" y2="21"/></svg>
                </ToolbarButton>

                <Divider colors={colors} />

                {/* Link */}
                <ToolbarButton title="Link" isActive={editorState?.link} onClick={handleLink} colors={colors}>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"/><path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"/></svg>
                </ToolbarButton>

                {enableFontSize && (
                    <>
                        <Divider colors={colors} />
                        <select
                            title="Font size"
                            value={editorState?.fontSize || ''}
                            onChange={(e) => {
                                const size = e.target.value;
                                if (size) editor.chain().focus().setFontSize(size).run();
                                else editor.chain().focus().unsetFontSize().run();
                            }}
                            style={{
                                height: 28,
                                padding: '0 4px',
                                borderRadius: 6,
                                border: `1px solid ${colors.border}`,
                                backgroundColor: 'transparent',
                                color: colors.textSecondary,
                                fontFamily: fonts.body,
                                fontSize: 12,
                                cursor: 'pointer',
                            }}
                        >
                            <option value="">Size</option>
                            {[12, 14, 16, 18, 20, 24, 28].map(n => (
                                <option key={n} value={`${n}px`}>{n}</option>
                            ))}
                        </select>
                    </>
                )}

                {enableTextAlign && (
                    <>
                        <Divider colors={colors} />
                        {['left', 'center', 'right'].map(align => (
                            <ToolbarButton
                                key={align}
                                title={`Align ${align}`}
                                isActive={align === 'left' ? editorState?.alignLeft : align === 'center' ? editorState?.alignCenter : editorState?.alignRight}
                                onClick={() => editor.chain().focus().setTextAlign(align).run()}
                                colors={colors}
                            >
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                    {align === 'left' && <><line x1="3" y1="6" x2="21" y2="6"/><line x1="3" y1="12" x2="15" y2="12"/><line x1="3" y1="18" x2="17" y2="18"/></>}
                                    {align === 'center' && <><line x1="3" y1="6" x2="21" y2="6"/><line x1="6" y1="12" x2="18" y2="12"/><line x1="4" y1="18" x2="20" y2="18"/></>}
                                    {align === 'right' && <><line x1="3" y1="6" x2="21" y2="6"/><line x1="9" y1="12" x2="21" y2="12"/><line x1="7" y1="18" x2="21" y2="18"/></>}
                                </svg>
                            </ToolbarButton>
                        ))}
                    </>
                )}
            </div>

            {/* Editor content */}
            <div className="tiptap-editor-content" style={{ backgroundColor: colors.surface, color: colors.text }}>
                <EditorContent editor={editor} />
            </div>
        </div>
    );
});

export default TiptapEditor;
